import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { X, MessageSquarePlus, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MessageList, type ChatMessage } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { ConversationHistory } from "./ConversationHistory";
import { useChatStream } from "@/hooks/useChatStream";
import { GetConversationsDocument } from "@/graphql/getConversations.api";
import { GetConversationDocument } from "@/graphql/getConversation.api";
import { cn } from "@/lib/utils";
import { BRAND_COLOR } from "@/lib/constants";

interface ChatPanelProps {
  onClose: () => void;
  isOpen?: boolean;
}

export function ChatPanel({ onClose, isOpen = false }: ChatPanelProps) {
  const { t } = useTranslation("common");
  const { slug } = useParams<{ slug: string }>();
  const [showHistory, setShowHistory] = useState(false);

  const { data: conversationsData, refetch: refetchConversations } = useQuery(GetConversationsDocument, {
    variables: { portfolioSlug: slug || "" },
    skip: !slug,
  });

  const {
    messages,
    isStreaming,
    conversationId,
    sendMessage,
    stopStreaming,
    loadConversation,
    startNewChat,
  } = useChatStream({
    portfolioSlug: slug || "",
    onConversationCreated: () => {
      // Refetch conversations when a new one is created
      refetchConversations();
    },
  });

  const { loading: loadingConversation } = useQuery(GetConversationDocument, {
    variables: { uuid: conversationId || "" },
    skip: !conversationId || messages.length > 0,
    onCompleted: (data) => {
      if (data.conversation?.messages) {
        loadConversation(data.conversation.id, data.conversation.messages as ChatMessage[]);
      }
    },
  });

  const handleNewChat = () => {
    startNewChat();
    setShowHistory(false);
  };

  const handleSelectConversation = useCallback(
    (id: string) => {
      loadConversation(id, []);
      setShowHistory(false);
    },
    [loadConversation]
  );

  const handleDeleteConversation = useCallback(
    (id: string) => {
      refetchConversations();
      // If we deleted the active conversation, start a new chat
      if (conversationId === id) {
        startNewChat();
      }
    },
    [refetchConversations, conversationId, startNewChat]
  );

  const handleRenameConversation = useCallback(() => {
    refetchConversations();
  }, [refetchConversations]);

  const conversations = conversationsData?.conversations || [];

  return (
    <div className="h-full flex flex-col bg-white border-l border-neutral-200">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-3 border-b border-neutral-200 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-neutral-800">
            A<span style={{ color: BRAND_COLOR }}>X</span> Assistant
          </span>
          {isStreaming && (
            <span className="text-xs text-neutral-500 animate-pulse">{t("chat.typing")}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleNewChat}>
                <MessageSquarePlus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("chat.newChat")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-8 w-8", showHistory && "bg-neutral-100")}
                onClick={() => setShowHistory(!showHistory)}
              >
                <History className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("chat.history")}</TooltipContent>
          </Tooltip>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {showHistory ? (
        <ConversationHistory
          conversations={conversations}
          activeId={conversationId}
          onSelect={handleSelectConversation}
          onNewChat={handleNewChat}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
        />
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <MessageList messages={messages} isLoading={loadingConversation} isStreaming={isStreaming} />
          <ChatInput
            onSend={sendMessage}
            disabled={!slug}
            isStreaming={isStreaming}
            onStop={stopStreaming}
            autoFocus={isOpen}
          />
        </div>
      )}
    </div>
  );
}
